import { booleanDataSocket, isBooleanDatasetSocket } from "../socket_types"
import { isCategoricalChainSeedingSuppressed } from "./categorical_dynamic_io_connections"

export interface CategoricalLabelRow {
  value: number
  label: string
}

function normalizeSavedRows(raw: unknown): CategoricalLabelRow[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((entry: any) => ({
      value: Number(entry?.value),
      label: String(entry?.label ?? "").trim(),
    }))
    .filter(entry => Number.isFinite(entry.value) && entry.value !== 255 && entry.label.length > 0)
    .sort((a, b) => a.value - b.value)
}

function getNodeById(editor: any, maybeNode: any): any | undefined {
  const nodeId =
    typeof maybeNode === "number"
      ? maybeNode
      : (typeof maybeNode?.id === "number" ? maybeNode.id : undefined)
  if (nodeId == null) return undefined
  return editor.nodes.find((n: any) => n.id === nodeId)
}

function resolveEditorComponent(editor: any, componentName: string): any {
  if (!editor || !componentName) return undefined
  try {
    const fromGet = editor.getComponent?.(componentName)
    if (fromGet) return fromGet
  } catch {
    // Rete throws when the name is unknown to getComponent.
  }
  return editor.components?.get?.(componentName)
}

function labelsFromNodeData(node: any): CategoricalLabelRow[] {
  const fromSplit = normalizeSavedRows(node?.data?.splitLabels)
  if (fromSplit.length > 0) return fromSplit

  const fromMerged = normalizeSavedRows(node?.data?.mergedLabels)
  if (fromMerged.length > 0) return fromMerged

  const fromDifference = normalizeSavedRows(node?.data?.differenceLabels)
  if (fromDifference.length > 0) return fromDifference

  const fromDataset = normalizeSavedRows(node?.data?.datasetLabels)
  if (fromDataset.length > 0) return fromDataset

  return []
}

function labelsFromBooleanOutputs(node: any): CategoricalLabelRow[] {
  const rows: CategoricalLabelRow[] = []
  Array.from(node.outputs.values()).forEach((output: any) => {
    const key = String(output.key ?? "")
    if (key === "All" || key === "out" || key === "Mask") return
    if (!isBooleanDatasetSocket(output.socket)) return
    const namedKeyMatch = /^label_(\d+)$/.exec(key)
    const classKeyMatch = /^class_(\d+)$/.exec(key)
    const value = namedKeyMatch
      ? Number(namedKeyMatch[1])
      : classKeyMatch
        ? Number(classKeyMatch[1])
        : rows.length + 1
    const label = String(output.name ?? "").trim()
    if (!label || value === 255) return
    rows.push({ value, label })
  })

  return rows.sort((a, b) => a.value - b.value)
}

/** Raster bool/cat inputs (e.g. UKCEH) expose class labels via component options, not node.data. */
function labelsFromRasterBoolCatOptions(editor: any, node: any): CategoricalLabelRow[] {
  const component = resolveEditorComponent(editor, node?.name)
  const options = component?.compProps?.options
  if (!Array.isArray(options)) return []
  return options
    .map((opt: { label?: string }, index: number) => ({
      value: index + 1,
      label: String(opt?.label ?? "").trim(),
    }))
    .filter(row => row.label.length > 0)
}

function labelsFromBooleanInputConnections(editor: any, node: any): CategoricalLabelRow[] {
  const rows: CategoricalLabelRow[] = []
  const used = new Set<string>()
  const ensureUnique = (label: string): string => {
    const base = label.trim() || "Untitled data"
    if (!used.has(base)) {
      used.add(base)
      return base
    }
    let idx = 2
    let next = `${base} (${idx})`
    while (used.has(next)) {
      idx += 1
      next = `${base} (${idx})`
    }
    used.add(next)
    return next
  }

  Array.from(node.inputs.values()).forEach((input: any) => {
    const connections = Array.isArray(input?.connections) ? input.connections : []
    connections.forEach((connection: any) => {
      const output = connection?.output
      if (!output || !isBooleanDatasetSocket(output.socket)) return
      const upstreamNode = getNodeById(editor, output.node ?? connection.node)
      const labelFromNodeData = String(upstreamNode?.data?.name ?? "").trim()
      const labelFromNodeType = String(upstreamNode?.name ?? "").trim()
      const labelFromOutput = String(output.name ?? "").trim()
      const preferredLabel = labelFromNodeData || labelFromOutput || labelFromNodeType || "Untitled data"
      const label = ensureUnique(preferredLabel)
      rows.push({ value: rows.length + 1, label })
    })
  })

  return rows
}

function dedupeRows(rows: CategoricalLabelRow[]): CategoricalLabelRow[] {
  const seenByNormalizedLabel = new Set<string>()
  const deduped: CategoricalLabelRow[] = []
  rows.forEach(row => {
    const normalized = row.label.trim().toLowerCase()
    if (!normalized || seenByNormalizedLabel.has(normalized)) return
    seenByNormalizedLabel.add(normalized)
    deduped.push(row)
  })
  return deduped
}

function resolveLabelsFromNode(editor: any, node: any, visited: Set<number>): CategoricalLabelRow[] {
  if (!node || visited.has(node.id)) return []
  visited.add(node.id)

  const directRows = labelsFromNodeData(node)
  if (directRows.length > 0) return directRows

  const fromBooleanOutputs = labelsFromBooleanOutputs(node)
  if (fromBooleanOutputs.length > 0) return fromBooleanOutputs

  const fromRasterBoolCat = labelsFromRasterBoolCatOptions(editor, node)
  if (fromRasterBoolCat.length > 0) return fromRasterBoolCat

  const categoricalUpstreamRows: CategoricalLabelRow[] = []
  Array.from(node.inputs.values()).forEach((input: any) => {
    if (isBooleanDatasetSocket(input?.socket)) return
    const firstConnection = input?.connections?.[0]
    if (!firstConnection) return
    const upstreamNode = getNodeById(editor, firstConnection.output?.node ?? firstConnection.node)
    if (!upstreamNode) return
    categoricalUpstreamRows.push(...resolveLabelsFromNode(editor, upstreamNode, visited))
  })
  const dedupedCategoricalUpstream = dedupeRows(categoricalUpstreamRows)
  if (dedupedCategoricalUpstream.length > 0) return dedupedCategoricalUpstream

  const fromBooleanInputs = labelsFromBooleanInputConnections(editor, node)
  if (fromBooleanInputs.length > 0) return fromBooleanInputs

  const booleanUpstreamRows: CategoricalLabelRow[] = []
  Array.from(node.inputs.values()).forEach((input: any) => {
    if (!isBooleanDatasetSocket(input?.socket)) return
    const firstConnection = input?.connections?.[0]
    if (!firstConnection) return
    const upstreamNode = getNodeById(editor, firstConnection.output?.node ?? firstConnection.node)
    if (!upstreamNode) return
    booleanUpstreamRows.push(...resolveLabelsFromNode(editor, upstreamNode, visited))
  })

  return dedupeRows(booleanUpstreamRows)
}

export function getCategoricalLabelsFromInputChain(editor: any, node: any, inputKey: string): CategoricalLabelRow[] {
  if (!editor || !node) return []
  const inputPort = (node.inputs as any).get?.(inputKey) ?? (node.inputs as any)[inputKey]
  const connection = inputPort?.connections?.[0]
  if (!connection) return []

  const upstreamNode = getNodeById(editor, connection.output?.node ?? connection.node)
  if (!upstreamNode) return []

  return resolveLabelsFromNode(editor, upstreamNode, new Set<number>())
}

const DYNAMIC_LABEL_OUTPUT_COMPONENTS = new Set([
  "Categorical to boolean datasets",
  "Categorical merge",
  "Categorical difference",
  "Categorical to numeric dataset",
])

/** Categorical passthrough nodes — refresh consumers beyond their direct `out` wire. */
const CATEGORICAL_PASSTHROUGH_OUTPUT_KEY: Record<string, string> = {
  Rename: "out",
  "Categorical merge": "All",
  "Categorical difference": "out",
}

function invokeDynamicLabelRefresh(editor: any, node: any): void {
  const component = resolveEditorComponent(editor, node.name)
  if (!component) return
  const refresh = (component as { refreshDynamicLabelOutputs?: (n: unknown) => void }).refreshDynamicLabelOutputs
  if (typeof refresh === "function") {
    refresh.call(component, node)
  } else {
    const seed = (component as { seedOutputsFromConnections?: (n: unknown) => void }).seedOutputsFromConnections
    seed?.call(component, node)
  }
  nodeUpdate(node)
}

/** Re-run dynamic label/boolean output seeding after Rete finishes updating connections. */
export function scheduleDynamicLabelOutputRefresh(editor: any, node: any): void {
  if (!editor || !node) return
  queueMicrotask(() => {
    if (isCategoricalChainSeedingSuppressed()) return
    invokeDynamicLabelRefresh(editor, node)
  })
}

/** Refresh every node that exposes dynamic per-class boolean outputs. */
export function scheduleAllDynamicLabelOutputRefreshes(editor: any): void {
  if (!editor) return
  queueMicrotask(() => {
    if (isCategoricalChainSeedingSuppressed()) return
    for (const node of editor.nodes) {
      if (!DYNAMIC_LABEL_OUTPUT_COMPONENTS.has(node.name)) continue
      invokeDynamicLabelRefresh(editor, node)
    }
  })
}

/** Refresh nodes downstream of a categorical passthrough output. */
export function scheduleDownstreamDynamicLabelOutputRefresh(
  editor: any,
  sourceNode: any,
  outputKey = "out"
): void {
  if (!editor || !sourceNode) return
  queueMicrotask(() => {
    if (isCategoricalChainSeedingSuppressed()) return
    refreshDownstreamDynamicLabelOutputs(editor, sourceNode, outputKey, new Set<number>())
    nodeUpdate(sourceNode)
  })
}

function refreshDownstreamDynamicLabelOutputs(
  editor: any,
  sourceNode: any,
  outputKey: string,
  visitedNodeIds: Set<number>
): void {
  if (!sourceNode || visitedNodeIds.has(sourceNode.id)) return
  visitedNodeIds.add(sourceNode.id)

  const outPort = (sourceNode.outputs as any).get?.(outputKey) ?? (sourceNode.outputs as any)[outputKey]
  const connections = Array.isArray(outPort?.connections) ? outPort.connections : []
  for (const connection of connections) {
    const targetNode = getNodeById(editor, connection?.input?.node)
    if (!targetNode) continue

    if (DYNAMIC_LABEL_OUTPUT_COMPONENTS.has(targetNode.name)) {
      invokeDynamicLabelRefresh(editor, targetNode)
    }

    const passthroughKey = CATEGORICAL_PASSTHROUGH_OUTPUT_KEY[targetNode.name]
    if (passthroughKey) {
      const component = resolveEditorComponent(editor, targetNode.name)
      const passthroughRefresh = (component as { refreshDynamicLabelOutputs?: (n: unknown) => void })
        .refreshDynamicLabelOutputs
      passthroughRefresh?.call(component, targetNode)
      refreshDownstreamDynamicLabelOutputs(editor, targetNode, passthroughKey, visitedNodeIds)
    } else if (!DYNAMIC_LABEL_OUTPUT_COMPONENTS.has(targetNode.name)) {
      nodeUpdate(targetNode)
    }
  }
}

function nodeUpdate(node: any): void {
  if (typeof node?.update === "function") {
    void node.update()
  }
}
